import { Link } from "react-router-dom";
import {
  GraduationCap,
  MapPin,
  Clock,
  Tag,
  BadgeCheck,
  Building2,
  Phone,
  Mail,
  ArrowRight,
} from "lucide-react";
import { COMPANY } from "../data/site";
import ContactCTA from "../components/ContactCTA";
import usePageMeta from "../hooks/usePageMeta";

const FACTS = [
  { icon: GraduationCap, label: "Utbildning", value: "Motorsågskörkort A + B" },
  { icon: MapPin, label: "Plats", value: "Laholm" },
  { icon: Clock, label: "Tidsåtgång", value: "Cirka 3 dagar" },
  { icon: Tag, label: "Pris", value: "7 995 kr / deltagare" },
  { icon: BadgeCheck, label: "Intyg", value: "Motorsågskörkort efter godkänd examination" },
  { icon: Building2, label: "Företag", value: "Företagsanpassade utbildningar erbjuds" },
];

const TARGET_AUDIENCE = [
  "Skogsägare",
  "Lantbrukare",
  "Fastighetsägare",
  "Entreprenörer",
  "Vedhuggare och privatpersoner",
  "Företag med personal som arbetar med motorsåg",
];

const EQUIPMENT = [
  "Motorsåg",
  "Hjälm med visir och hörselskydd",
  "Sågskyddsbyxor",
  "Skyddskängor eller skyddsstövlar",
  "Handskar",
  "Varseljacka",
  "Fällkilar",
  "Slägga eller yxa",
  "Filutrustning",
  "Måttband",
  "Första hjälpen-kit",
];

export default function Motorsagskorkort() {
  usePageMeta({
    title: "Motorsågskörkort A+B i Laholm | Trädfällarna Laholm AB",
    description:
      "Motorsågskörkort A + B i Laholm enligt Säker Skogs riktlinjer. Cirka 3 dagar, 7 995 kr per deltagare. Företagsanpassade utbildningar erbjuds.",
  });

  return (
    <div data-testid="motorsagskorkort-page" className="bg-[#F7F5F0]">
      {/* HERO */}
      <section className="relative min-h-[480px] md:min-h-[580px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/motorsagskorkort-2026.webp"
            alt="Motorsåg i arbete"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 tf-hero-overlay" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 text-white">
          <span className="eyebrow !text-[#F2A57C]">Utbildning</span>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight max-w-4xl">
            Motorsågskörkort A+B i Laholm
          </h1>
          <p className="mt-5 text-[#E5E7E3] text-lg leading-relaxed max-w-3xl">
            Utbildning enligt Säker Skogs riktlinjer för privatpersoner,
            skogsägare, lantbrukare, entreprenörer och företag.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Link
              to="/kontakt#offert"
              data-testid="msk-cta-book"
              className="inline-flex items-center justify-center gap-2 bg-[#D95D2E] hover:bg-[#B84B22] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors"
            >
              Boka utbildning <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={`tel:${COMPANY.educationContact.phoneE164}`}
              data-testid="msk-cta-phone"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/80 hover:bg-white hover:text-[#1B2A1E] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors"
            >
              <Phone className="w-4 h-4" /> Fråga om nästa kursdatum
            </a>
          </div>
        </div>
      </section>

      {/* FACT CARDS */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <span className="eyebrow">Snabbfakta</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold text-[#1B2A1E] leading-tight">
              Det här ingår i utbildningen
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
            {FACTS.map(({ icon: Icon, label, value }, i) => (
              <div
                key={label}
                data-testid={`msk-fact-${i + 1}`}
                className="bg-white border border-[#E2E0D8] rounded-xl p-6 flex items-start gap-4"
              >
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-md bg-[#1B2A1E] text-[#D95D2E] shrink-0">
                  <Icon className="w-5 h-5" strokeWidth={2.2} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] font-bold text-[#5A6258] mb-1">
                    {label}
                  </div>
                  <div className="font-heading font-bold text-[#1B2A1E] text-lg leading-snug">
                    {value}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* A & B levels */}
      <section className="section-pad bg-[#EFEBDF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          <div data-testid="msk-level-a" className="bg-white rounded-xl p-8 border border-[#E2E0D8]">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-[#D95D2E] text-white font-heading text-2xl font-extrabold">
                A
              </span>
              <h3 className="font-heading text-2xl font-extrabold text-[#1B2A1E]">
                Motorsågskörkort A
              </h3>
            </div>
            <p className="text-[#3a4338] text-base leading-relaxed">
              A-nivån ger grundläggande kunskap i säker hantering av motorsåg,
              arbetsmiljö, riskbedömning, kapning, skyddsutrustning samt skötsel
              och underhåll.
            </p>
          </div>
          <div data-testid="msk-level-b" className="bg-white rounded-xl p-8 border border-[#E2E0D8]">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-[#1B2A1E] text-white font-heading text-2xl font-extrabold">
                B
              </span>
              <h3 className="font-heading text-2xl font-extrabold text-[#1B2A1E]">
                Motorsågskörkort B
              </h3>
            </div>
            <p className="text-[#3a4338] text-base leading-relaxed">
              B-nivån fokuserar på säker trädfällning, planering, fällteknik,
              riskzoner och praktiskt arbete i skogsmiljö.
            </p>
          </div>
        </div>
      </section>

      {/* Audience + equipment */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <div>
            <span className="eyebrow">För vem</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold text-[#1B2A1E] leading-tight">
              Vem passar utbildningen för?
            </h2>
            <ul className="mt-6 space-y-2">
              {TARGET_AUDIENCE.map((t, i) => (
                <li
                  key={t}
                  data-testid={`msk-audience-${i + 1}`}
                  className="flex items-center gap-3 bg-white border border-[#E2E0D8] rounded-lg px-4 py-3"
                >
                  <span className="w-2 h-2 rounded-full bg-[#D95D2E] shrink-0" />
                  <span className="text-[#18221A] font-medium">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <span className="eyebrow">Utrustning</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold text-[#1B2A1E] leading-tight">
              Vad du behöver ha med dig
            </h2>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {EQUIPMENT.map((t, i) => (
                <li
                  key={t}
                  data-testid={`msk-equipment-${i + 1}`}
                  className="flex items-center gap-2.5 bg-[#EFEBDF] rounded-md px-3 py-2.5"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1B2A1E] shrink-0" />
                  <span className="text-sm text-[#18221A] font-medium leading-tight">
                    {t}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Education contact */}
      <section className="section-pad bg-[#1B2A1E] text-white tf-grain relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="eyebrow !text-[#F2A57C]">Kontakt utbildning</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold leading-tight">
              Prata direkt med Joakim
            </h2>
            <p className="mt-5 text-[#D9DCD3] text-lg leading-relaxed max-w-xl">
              Hör av dig om datum, företagsanpassade kurser eller om du undrar
              något inför utbildningen. Vi svarar gärna.
            </p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 lg:p-8 backdrop-blur-sm">
            <div className="text-xs uppercase tracking-[0.16em] font-bold text-[#F2A57C] mb-2">
              Utbildningsansvarig
            </div>
            <div className="font-heading text-2xl font-extrabold text-white mb-5">
              {COMPANY.educationContact.name}
            </div>
            <div className="space-y-3">
              <a
                href={`tel:${COMPANY.educationContact.phoneE164}`}
                data-testid="msk-contact-phone"
                className="flex items-center gap-3 text-white hover:text-[#F2A57C] transition-colors"
              >
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-[#D95D2E] text-white">
                  <Phone className="w-4 h-4" />
                </span>
                <span className="font-heading font-bold text-lg">
                  {COMPANY.educationContact.phone}
                </span>
              </a>
              <a
                href={`mailto:${COMPANY.educationContact.email}`}
                data-testid="msk-contact-email"
                className="flex items-center gap-3 text-white hover:text-[#F2A57C] transition-colors"
              >
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-[#D95D2E] text-white">
                  <Mail className="w-4 h-4" />
                </span>
                <span className="font-heading font-bold text-lg">
                  {COMPANY.educationContact.email}
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA
        title="Vill du gå motorsågskörkort?"
        text="Vi planerar löpande nya kurstillfällen och ordnar gärna företagsanpassade utbildningar. Skicka en förfrågan så återkommer vi med datum och upplägg."
      />
    </div>
  );
}
